package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.SizeRule
import dev.lingvo.shared.screenshots.design.derive.MagicResize
import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.CanvasSize
import dev.lingvo.shared.screenshots.doc.ColorRefs
import dev.lingvo.shared.screenshots.doc.DESIGN_SCHEMA_VERSION
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FitSpec
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageFit
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.ShapeKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextContent
import kotlin.math.roundToInt
import dev.lingvo.shared.screenshots.templates.Template
import dev.lingvo.shared.screenshots.templates.withBackgroundImage
import dev.lingvo.shared.screenshots.templates.withoutBackgroundImage
import dev.lingvo.shared.screenshots.templates.TemplateSlot
import dev.lingvo.shared.screenshots.text.FontCatalog
import dev.lingvo.site.components.studio.preview.DesignCatalogPort
import dev.lingvo.shared.ScreenshotDesignDto

/** All parametric edits pass the same codec as persistence before becoming visible or saveable. */
class DesignModel(private val plan: ScreenshotPlanDto, private val looks: DesignCatalogPort) {
    /** A board is sized for its store, never inferred from an arbitrary raw capture. */
    fun build(layout: Template, brand: BrandKit): ScreenshotDesign {
        val palette = looks.palette(layout, brand)
        val boards = devices().mapIndexed { index, device ->
            val board = Board("board-$index", device.id, storeCanvas(device), emptyList(), layout.mirrorRtl)
            board.copy(pages = layout.build(brand, board, slots(device), palette))
        }
        return checked(ScreenshotDesign(DESIGN_SCHEMA_VERSION, plan.platform, layout.id, brand, palette, boards))
    }

    /** Rebuilding preserves slot bindings and reapplies the person's presentation choices. */
    fun switchLayout(design: ScreenshotDesign, layout: Template): ScreenshotDesign {
        val rebuilt = build(layout, design.brand)
        val result = checked(rebuilt.copy(palette = design.palette, boards = rebuilt.boards.map { board ->
            val previous = design.boards.firstOrNull { it.deviceClass == board.deviceClass }
            val frame = previous?.pages?.flatMap { layers(it.layers) }?.filterIsInstance<DeviceLayer>()?.firstOrNull()?.frame
            board.copy(pages = board.pages.map { page ->
                val old = previous?.pages?.firstOrNull { page.slotIds.first() in it.slotIds }
                var result = page
                if (frame != null) result = result.copy(layers = mapLayers(result.layers) {
                    if (it is DeviceLayer) it.copy(frame = frame) else it
                })
                old?.layers?.filterIsInstance<BackgroundLayer>()?.firstOrNull()?.let { background ->
                    result = result.copy(layers = result.layers.map { if (it is BackgroundLayer) it.copy(fill = background.fill) else it })
                }
                old?.layers?.filterIsInstance<ImageLayer>()?.firstOrNull { isDesignBackgroundImage(it) }?.let { image ->
                    result = withBackgroundImage(result, (image.source as ImageSource.Asset).assetId, false, board.canvas)
                }
                val fit = old?.let { layers(it.layers).filterIsInstance<TextLayer>().firstOrNull { layer -> layer.content is TextContent.SlotCaption }?.fit }
                if (fit != null) result = result.copy(layers = mapLayers(result.layers) { if (it is TextLayer && it.content is TextContent.SlotCaption) it.copy(fit = fit) else it })
                if (old?.layers?.any { isDesignBackgroundScrim(it) } == true) result = setBackgroundScrim(result, board.canvas, true)
                result
            })
        }))
        return plan.slots.sortedBy { it.position }.foldIndexed(result) { index, current, slot ->
            design.palette.slotAccents.getOrNull(index)?.let { slotAccent(current, slot.key, it) } ?: current
        }
    }

    /** Compatibility comes from the frame engine, including the frameless option. */
    fun frame(design: ScreenshotDesign, device: DeviceClass, frame: FrameKind): ScreenshotDesign {
        require(frame in looks.framesFor(device)) { "Choose a frame offered for this device." }
        return editBoard(design, device) { board -> board.copy(pages = board.pages.map { page ->
            page.copy(layers = mapLayers(page.layers) { if (it is DeviceLayer) it.copy(frame = frame) else it })
        }) }
    }

    /** Re-derives semantic colours without changing content, assets or geometry. */
    fun brand(design: ScreenshotDesign, primary: String?, secondary: String?, iconPalette: List<String> = design.brand.iconPalette): ScreenshotDesign {
        require(listOfNotNull(primary, secondary).all(ColorRefs::isOpaqueHex)) { "Use a six-digit hex colour." }
        val brand = design.brand.copy(primary = primary, secondary = secondary, iconPalette = iconPalette.take(5))
        val template = looks.templates().firstOrNull { it.id == design.templateId } ?: looks.layouts().firstOrNull()
        requireNotNull(template) { "The design's template is unavailable. Try again when the catalog loads." }
        return checked(design.copy(brand = brand, palette = looks.palette(template, brand).let {
            if (design.palette.slotAccents.isEmpty()) it else it.copy(slotAccents = design.palette.slotAccents)
        }))
    }

    /** Catalog-only names prevent arbitrary CSS families or unsupported uploaded fonts. */
    fun fonts(design: ScreenshotDesign, display: String?, text: String?): ScreenshotDesign {
        require(listOfNotNull(display, text).all { FontCatalog.byName(it) != null }) { "Choose a font from the catalog." }
        return checked(design.copy(brand = design.brand.copy(displayFamily = display, textFamily = text)))
    }

    /** Validation enforces finite sizes, the engine range and floor ≤ maximum. */
    fun captionSizes(design: ScreenshotDesign, device: DeviceClass, maximum: Double, floor: Double): ScreenshotDesign =
        editBoard(design, device) { board -> board.copy(pages = board.pages.map { page ->
            page.copy(layers = mapLayers(page.layers) {
                if (it is TextLayer && it.content is TextContent.SlotCaption) it.copy(fit = it.fit.copy(maxSize = maximum, floorSize = floor)) else it
            })
        }) }

    /** Slot accents follow plan order, so a long stable key is never used as a layer identifier. */
    fun slotAccent(design: ScreenshotDesign, slotKey: String, color: String): ScreenshotDesign {
        require(ColorRefs.isOpaqueHex(color)) { "Use a six-digit hex colour." }
        val ordered = plan.slots.sortedBy { it.position }
        val index = ordered.indexOfFirst { it.key == slotKey }
        require(index >= 0) { "Choose a slot in this plan." }
        val accents = ordered.indices.map { design.palette.slotAccents.getOrNull(it) ?: design.palette.accent }.toMutableList()
        accents[index] = color
        val previous = design.palette.slotAccents.getOrNull(index)
        return checked(design.copy(palette = design.palette.copy(slotAccents = accents), boards = design.boards.map { board ->
            board.copy(pages = board.pages.map { page -> page.copy(layers = mapLayers(page.layers) { layer ->
                if (page.slotIds.getOrNull(layer.base.roleSlot) == slotKey) recolorSlot(layer, color, previous) else layer
            }) })
        }))
    }

    /** Palette and gradient fills remove only the dedicated full-bleed image and its scrim. */
    fun background(design: ScreenshotDesign, device: DeviceClass, fill: Paint): ScreenshotDesign = editBoard(design, device) { board ->
        board.copy(pages = board.pages.map { page -> removeBackgroundArtwork(page).let { clean -> clean.copy(layers = clean.layers.map {
            if (it is BackgroundLayer) it.copy(fill = fill) else it
        }) } })
    }

    /** Uploaded artwork is project-scoped by id; URLs never enter the document. */
    fun backgroundImage(design: ScreenshotDesign, device: DeviceClass, assetId: String): ScreenshotDesign {
        require(Regex("^[a-z0-9][a-z0-9-]{0,63}$").matches(assetId)) { "Choose a valid uploaded image." }
        return editBoard(design, device) { board ->
            board.copy(pages = board.pages.map { withBackgroundImage(removeLegacyArtwork(it), assetId, it.layers.any(::isDesignBackgroundScrim), board.canvas) })
        }
    }

    /** A separate scrim layer keeps artwork intact and makes contrast a reversible choice. */
    fun scrim(design: ScreenshotDesign, device: DeviceClass, enabled: Boolean): ScreenshotDesign {
        return editBoard(design, device) { board ->
            board.copy(pages = board.pages.map { setBackgroundScrim(it, board.canvas, enabled) })
        }
    }

    /** Copies presentation through the template at the destination's aspect rather than stretching raw pixels. */
    fun copyLayout(design: ScreenshotDesign, from: DeviceClass, to: DeviceClass): ScreenshotDesign {
        require(design.boards.any { it.deviceClass == from.id }) { "The source device has no layout." }
        val template = requireNotNull(looks.templates().firstOrNull { it.id == design.templateId }) { "The design's template is unavailable." }
        val usedIds = layerIds(design).apply {
            addAll(design.boards.map { it.id })
            addAll(design.boards.flatMap { it.pages }.map { it.id })
        }
        val board = Board(nextLayerId(usedIds, "board"), to.id, storeCanvas(to), emptyList(), template.mirrorRtl)
        val destination = board.copy(pages = template.build(design.brand, board, slots(to), design.palette))
        val source = design.boards.first { it.deviceClass == from.id }
        val frame = source.pages.flatMap { layers(it.layers) }.filterIsInstance<DeviceLayer>().firstOrNull()?.frame
        var copied = checked(design.copy(boards = design.boards.filterNot { it.deviceClass == to.id } + destination))
        val sourceLayers = source.pages.firstOrNull()?.layers.orEmpty()
        sourceLayers.filterIsInstance<BackgroundLayer>().firstOrNull()?.let { copied = background(copied, to, it.fill) }
        sourceLayers.filterIsInstance<ImageLayer>().firstOrNull { isDesignBackgroundImage(it) }?.let {
            copied = backgroundImage(copied, to, (it.source as ImageSource.Asset).assetId)
        }
        layers(sourceLayers).filterIsInstance<TextLayer>().firstOrNull { it.content is TextContent.SlotCaption }?.let {
            copied = captionSizes(copied, to, it.fit.maxSize, it.fit.floorSize)
        }
        if (sourceLayers.any { isDesignBackgroundScrim(it) }) copied = scrim(copied, to, true)
        return if (frame != null && frame in looks.framesFor(to)) frame(copied, to, frame) else copied
    }

    private fun devices(): List<DeviceClass> = plan.slots.sortedBy { it.position }.flatMap { it.devices }.distinct()
    private fun slots(device: DeviceClass): List<TemplateSlot> = plan.slots.sortedBy { it.position }
        .filter { device in it.devices }.map { TemplateSlot(it.key, it.accent) }
    private fun editBoard(design: ScreenshotDesign, device: DeviceClass, edit: (Board) -> Board): ScreenshotDesign =
        checked(design.copy(boards = design.boards.map { if (it.deviceClass == device.id) edit(it) else it }))
}

/** Recognize previously saved artwork while all new edits use the shared helper. */
internal fun isDesignBackgroundImage(layer: Layer): Boolean = layer is ImageLayer &&
    layer.source is ImageSource.Asset && layer.base.name in setOf("Background image", "Background artwork")
internal fun isDesignBackgroundScrim(layer: Layer): Boolean = layer.base.name in setOf("Background scrim", "Background artwork scrim")
private fun removeLegacyArtwork(page: Page): Page {
    val removed = page.layers.filter { it.base.name in setOf("Background image", "Background scrim") }.map { it.base.id }.toSet()
    return page.copy(layers = page.layers.filterNot { it.base.id in removed }, overrides = page.overrides.mapValues { (_, value) ->
        value.copy(patches = value.patches.filterKeys { it !in removed })
    })
}
private fun removeBackgroundArtwork(page: Page): Page = withoutBackgroundImage(removeLegacyArtwork(page))
private fun setBackgroundScrim(page: Page, canvas: CanvasSize, enabled: Boolean): Page {
    val image = page.layers.firstOrNull(::isDesignBackgroundImage) as? ImageLayer ?: return page
    return withBackgroundImage(removeLegacyArtwork(page), (image.source as ImageSource.Asset).assetId, enabled, canvas)
}
private fun layerIds(design: ScreenshotDesign): MutableSet<String> = design.boards.flatMap { it.pages }.flatMap { layers(it.layers) }.map { it.base.id }.toMutableSet()
private fun nextLayerId(ids: MutableSet<String>, prefix: String): String =
    generateSequence(0) { it + 1 }.map { "$prefix-$it" }.first { it !in ids }.also(ids::add)
internal fun layers(items: List<Layer>): List<Layer> = items.flatMap { if (it is GroupLayer) listOf(it) + layers(it.children) else listOf(it) }
private fun mapLayers(items: List<Layer>, edit: (Layer) -> Layer): List<Layer> = items.map {
    edit(if (it is GroupLayer) it.copy(children = mapLayers(it.children, edit)) else it)
}
internal fun checked(design: ScreenshotDesign): ScreenshotDesign = when (val result = DesignCodec.decode(DesignCodec.encode(design), StoreDeviceClassRule)) {
    is DecodeResult.Ok -> result.design
    is DecodeResult.Refused -> throw IllegalArgumentException(result.issues.joinToString("\n") { "${it.path}: ${it.message}" })
}
internal fun storeCanvas(device: DeviceClass): CanvasSize = when (val rule = device.spec.storeRule) {
    is SizeRule.Exact -> rule.sizes.first().let { CanvasSize(it.width, it.height) }
    is SizeRule.Range -> {
        val aspect = rule.aspects.firstOrNull()
        if (aspect != null) {
            val short = minOf(aspect.width, aspect.height)
            val minimumScale = (rule.minSide + short - 1) / short
            val maximumScale = rule.maxSide / maxOf(aspect.width, aspect.height)
            val preferredShort = if (device == DeviceClass.PLAY_WEAR) 512 else 1080
            val scale = ((preferredShort + short - 1) / short).coerceIn(minimumScale, maximumScale)
            CanvasSize(aspect.width * scale, aspect.height * scale)
        }
        else if (device == DeviceClass.PLAY_WEAR) CanvasSize(512, 512)
        else CanvasSize(1080, 1920)
    }
}

/** Save progress includes explicit recovery states rather than pretending a refused write succeeded. */
enum class DesignSaveState { CLEAN, DIRTY, SAVING, CONFLICT, OFFLINE, FAILED }

/** Pure transitions retain newer edits across an in-flight acknowledgement. */
internal data class DesignAutosaveSnapshot(
    val document: ScreenshotDesign?,
    val baseVersion: Int,
    val state: DesignSaveState = DesignSaveState.CLEAN,
    val revision: Int = 0,
    val savingRevision: Int? = null,
    val conflict: ScreenshotDesignDto? = null,
) {
    fun edit(value: ScreenshotDesign): DesignAutosaveSnapshot = if (value == document) this else copy(
        document = value, revision = revision + 1,
        state = if (state == DesignSaveState.CONFLICT) state else DesignSaveState.DIRTY,
    )

    fun begin(online: Boolean): DesignAutosaveSnapshot =
        if (document == null || state == DesignSaveState.CLEAN || state == DesignSaveState.CONFLICT || savingRevision != null) this
        else if (!online) copy(state = DesignSaveState.OFFLINE)
        else copy(state = DesignSaveState.SAVING, savingRevision = revision)

    /** Server refreshes merge only into untouched boards, preserving edits made during the request. */
    fun saved(dto: ScreenshotDesignDto, canonical: ScreenshotDesign, sent: ScreenshotDesign? = null): DesignAutosaveSnapshot = copy(
        document = if (savingRevision == revision) canonical else document?.let { local ->
            if (sent == null) local else MagicResize.mergeAcknowledged(sent, local, canonical)
        },
        baseVersion = dto.version, savingRevision = null,
        state = if (savingRevision == revision) DesignSaveState.CLEAN else DesignSaveState.DIRTY,
    )

    fun failed(offline: Boolean, conflicted: Boolean): DesignAutosaveSnapshot = copy(
        savingRevision = null, state = when {
            conflicted -> DesignSaveState.CONFLICT
            offline -> DesignSaveState.OFFLINE
            else -> DesignSaveState.FAILED
        },
    )

    fun keepMine(): DesignAutosaveSnapshot = conflict?.let {
        copy(baseVersion = it.version, conflict = null, state = DesignSaveState.DIRTY)
    } ?: this

    fun loadTheirs(value: ScreenshotDesign): DesignAutosaveSnapshot = conflict?.let {
        copy(document = value, baseVersion = it.version, conflict = null, state = DesignSaveState.CLEAN,
            savingRevision = null, revision = revision + 1)
    } ?: this
}

/** Makes the idle-save policy independently testable without browser timers. */
internal class DesignSaveScheduler(
    writable: Boolean,
    private val needsSave: () -> Boolean,
    private val afterIdle: (() -> Unit) -> Unit,
    private val cancelIdle: () -> Unit,
    private val save: () -> Unit,
) {
    var writable: Boolean = writable
        set(value) {
            if (field == value) return
            field = value
            if (value) restart() else cancelIdle()
        }

    fun restart() {
        cancelIdle()
        if (writable && needsSave()) afterIdle(save)
    }
}


private fun recolorSlot(layer: Layer, color: String, previous: String?): Layer {
    fun ref(value: String): String = when {
        value == "@slot" || previous != null && value == previous -> color
        value.startsWith("@slot/") -> color + ((value.substringAfter('/').toDouble() * 255).roundToInt()).toString(16).padStart(2, '0').uppercase()
        previous != null && value.startsWith(previous) && value.length == 9 -> color + value.takeLast(2)
        else -> value
    }
    fun paint(value: Paint): Paint = when (value) {
        is Paint.Solid -> value.copy(color = ref(value.color))
        is Paint.Linear -> value.copy(stops = value.stops.map { it.copy(color = ref(it.color)) })
        is Paint.Radial -> value.copy(stops = value.stops.map { it.copy(color = ref(it.color)) })
    }
    val base = layer.base.copy(shadow = layer.base.shadow?.let { it.copy(color = ref(it.color)) })
    return when (layer) {
        is BackgroundLayer -> layer.copy(base = base, fill = paint(layer.fill), blobs = layer.blobs.map { it.copy(color = ref(it.color)) })
        is ShapeLayer -> layer.copy(base = base, fill = layer.fill?.let(::paint), stroke = layer.stroke?.let { it.copy(paint = paint(it.paint)) })
        is TextLayer -> layer.copy(base = base, style = layer.style.copy(color = paint(layer.style.color)))
        is ImageLayer -> layer.copy(base = base, stroke = layer.stroke?.let { it.copy(paint = paint(it.paint)) })
        is DeviceLayer -> layer.copy(base = base)
        is GroupLayer -> layer.copy(base = base)
    }
}
